import { useApi } from '@hero/context';
import type { UseAddressApi, UseAutocompleteApi, UseGoogleSearchApi } from './types';
import { useMutationAPI, useQueryAPI } from '../../utils';

/**
 * One-off fetch for address from lat/lng (reverse geocode).
 * Uses useMutationAPI with invalidationStrategy: 'none'. Map calls mutateAsync({ lat, lng }).
 * API from ApiProvider (useApi()); pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UseAddressApi}
 */
export const useAddressApi: UseAddressApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'address',
    apiFn: api.common.location.address,
    defaultInvalidationStrategy: 'none',
    apiConfig,
  });
};

/**
 * Location autocomplete (search). Query runs when query.search is non-empty; results cached by search.
 * API from ApiProvider (useApi()); pass apiConfig to override apiFn, enabled, etc.
 *
 * Hook API: {@link UseAutocompleteApi}
 */
export const useAutocompleteApi: UseAutocompleteApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'autocomplete',
    apiFn: api.common.location.autocomplete,
    query,
    apiConfig,
    isEnabled: () => apiConfig?.enabled !== false && query.search.trim().length > 0,
  });
};

/**
 * Google Places location search by text. Returns matching locations.
 * Uses useMutationAPI with invalidationStrategy: 'none'. Call mutateAsync({ search }).
 * API from ApiProvider (useApi()); pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UseGoogleSearchApi}
 */
export const useGoogleSearchApi: UseGoogleSearchApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'googleSearch',
    apiFn: api.common.location.search,
    defaultInvalidationStrategy: 'none',
    apiConfig,
  });
};
